import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Save, Star, Search, ExternalLink, Upload, ImagePlus } from "lucide-react";
import { fetchArticles } from "../lib/api";
import { adminCreateArticle, adminUpdateArticle, adminDeleteArticle, adminUploadImage } from "../lib/api";

const CATS = ["football", "basketball", "draft", "teams", "news"];

const EMPTY = {
  slug: "",
  title: "",
  dek: "",
  category: "football",
  tags: "",
  author: "",
  hero_image: "",
  body: "",
  read_minutes: 5,
  featured: false,
};

export default function ArticlesPanel({ token }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, {} = new, {slug:...} = edit
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchArticles({ limit: 100 });
      setArticles(r.articles || []);
    } catch { toast.error("Couldn't load articles."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? Can't be undone.`)) return;
    try {
      await adminDeleteArticle(token, slug);
      toast.success("Article deleted.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  const filtered = articles.filter((a) =>
    !q ||
    a.title.toLowerCase().includes(q.toLowerCase()) ||
    a.slug.toLowerCase().includes(q.toLowerCase()) ||
    a.category.toLowerCase().includes(q.toLowerCase())
  );

  if (editing !== null) {
    return <ArticleEditor token={token}
                          initial={editing}
                          onCancel={() => setEditing(null)}
                          onSaved={() => { setEditing(null); load(); }} />;
  }

  return (
    <div data-testid="admin-articles-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-kicker">Editorial Hub</div>
          <h2 className="text-headline text-3xl md:text-4xl">Articles</h2>
          <p className="text-dek mt-1">Publish a recap. Edit a draft. Drop a hot take.</p>
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2 border-2 border-[var(--ink)] px-3 py-2 bg-[var(--paper)]">
            <Search size={14} className="text-[var(--muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   className="bg-transparent outline-none text-sm w-40 md:w-64"
                   placeholder="Filter…"
                   data-testid="articles-filter-input" />
          </div>
          <button onClick={() => setEditing(EMPTY)} className="btn-rust" data-testid="articles-new-button">
            <Plus size={14} /> New Article
          </button>
        </div>
      </div>

      <div className="border-2 border-[var(--ink)] overflow-hidden" data-testid="articles-table">
        <div className="grid grid-cols-12 bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-widest text-xs px-4 py-3">
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Author</div>
          <div className="col-span-1">Featured</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-8 text-center font-mono-ed uppercase text-xs tracking-widest text-[var(--muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center font-mono-ed uppercase text-xs tracking-widest text-[var(--muted)]">No articles match.</div>
        ) : (
          <div className="divide-y divide-[var(--line)]">
            {filtered.map((a) => (
              <div key={a.slug} className="grid grid-cols-12 px-4 py-3 text-sm items-center hover:bg-[var(--paper-deep)]/40"
                   data-testid={`articles-row-${a.slug}`}>
                <div className="col-span-5">
                  <div className="font-semibold truncate">{a.title}</div>
                  <div className="text-xs text-[var(--muted)] font-mono-ed truncate">{a.slug}</div>
                </div>
                <div className="col-span-2">
                  <span className="chip text-[0.65rem]">{a.category}</span>
                </div>
                <div className="col-span-2 truncate text-[var(--muted)] text-xs">{a.author}</div>
                <div className="col-span-1">
                  {a.featured && <Star size={14} className="text-[var(--rust)] fill-[var(--rust)]" />}
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <a href={`/editorial/${a.slug}`} target="_blank" rel="noreferrer"
                     className="p-2 hover:text-[var(--rust)]"
                     title="View live"
                     data-testid={`articles-view-${a.slug}`}>
                    <ExternalLink size={14} />
                  </a>
                  <button onClick={() => setEditing(a)}
                          className="p-2 hover:text-[var(--rust)]"
                          title="Edit"
                          data-testid={`articles-edit-${a.slug}`}>
                    <Edit3 size={14} />
                  </button>
                  <button onClick={() => remove(a.slug)}
                          className="p-2 hover:text-red-700"
                          title="Delete"
                          data-testid={`articles-delete-${a.slug}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ArticleEditor({ token, initial, onCancel, onSaved }) {
  const isNew = !initial.slug;
  const [form, setForm] = useState({
    slug: initial.slug || "",
    title: initial.title || "",
    dek: initial.dek || "",
    category: initial.category || "football",
    tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
    author: initial.author || "",
    hero_image: initial.hero_image || "",
    body: initial.body || "",
    read_minutes: initial.read_minutes || 5,
    featured: !!initial.featured,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked
            : e.target.type === "number" ? Number(e.target.value) || 0
            : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.dek || !form.body || !form.author || !form.hero_image) {
      toast.error("Title, dek, author, hero image, and body are all required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (isNew) {
        const created = await adminCreateArticle(token, payload);
        toast.success(`Published. /editorial/${created.slug}`);
      } else {
        await adminUpdateArticle(token, initial.slug, payload);
        toast.success("Article updated.");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="article-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-kicker">{isNew ? "New" : "Editing"}</div>
          <h2 className="text-headline text-3xl md:text-4xl">
            {isNew ? "Drop a New Story" : form.title || "Untitled"}
          </h2>
        </div>
        <button onClick={onCancel} className="btn-ghost text-xs" data-testid="article-editor-cancel">
          <X size={14} /> Cancel
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 sticker-card p-6 md:p-8 space-y-5">
          <div>
            <label className="text-kicker block mb-1">Title *</label>
            <input className="field" required value={form.title} onChange={set("title")}
                   data-testid="article-title-input" />
          </div>
          <div>
            <label className="text-kicker block mb-1">Slug (auto if blank)</label>
            <input className="field" value={form.slug} onChange={set("slug")}
                   placeholder="auto-generated-from-title"
                   data-testid="article-slug-input" />
          </div>
          <div>
            <label className="text-kicker block mb-1">Dek (subhead) *</label>
            <textarea className="field min-h-[60px] resize-y" required
                      value={form.dek} onChange={set("dek")}
                      data-testid="article-dek-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-kicker block mb-1">Category *</label>
              <select className="field" value={form.category} onChange={set("category")}
                      data-testid="article-category-select">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-kicker block mb-1">Author *</label>
              <input className="field" required value={form.author} onChange={set("author")}
                     placeholder="Dee Shrop"
                     data-testid="article-author-input" />
            </div>
          </div>
          <div>
            <label className="text-kicker block mb-1">Hero Image *</label>
            <ImageUploader
              token={token}
              value={form.hero_image}
              onChange={(url) => setForm({ ...form, hero_image: url })}
            />
          </div>
          <div>
            <label className="text-kicker block mb-1">Tags (comma-separated)</label>
            <input className="field" value={form.tags} onChange={set("tags")}
                   placeholder="nfl, recap, playoffs"
                   data-testid="article-tags-input" />
          </div>
          <div>
            <label className="text-kicker block mb-1">Body * (use **bold** and blank lines for paragraphs)</label>
            <textarea className="field min-h-[280px] resize-y font-serif-ed text-base" required
                      value={form.body} onChange={set("body")}
                      data-testid="article-body-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-kicker block mb-1">Read Minutes</label>
              <input className="field" type="number" min="1" max="60"
                     value={form.read_minutes} onChange={set("read_minutes")}
                     data-testid="article-readmin-input" />
            </div>
            <label className="flex items-end gap-2 pb-2 cursor-pointer" data-testid="article-featured-toggle">
              <input type="checkbox" className="w-5 h-5 accent-[var(--rust)]"
                     checked={form.featured} onChange={set("featured")} />
              <span className="text-kicker">Featured (Home + Top of Hub)</span>
            </label>
          </div>

          <div className="flex flex-wrap gap-3 pt-3 border-t-2 border-[var(--ink)]">
            <button type="submit" className="btn-rust" disabled={saving} data-testid="article-save-button">
              <Save size={14} /> {saving ? "Saving…" : isNew ? "Publish" : "Save Changes"}
            </button>
            <button type="button" onClick={onCancel} className="btn-ghost" data-testid="article-editor-back">
              Back
            </button>
          </div>
        </form>

        <aside className="lg:col-span-2">
          <div className="border-2 border-[var(--ink)] bg-[var(--paper)]" data-testid="article-preview-card">
            <div className="aspect-[16/10] bg-[var(--ink)] overflow-hidden">
              {form.hero_image ? (
                <img src={form.hero_image} alt="hero" className="w-full h-full object-cover"
                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[var(--paper)] text-xs uppercase tracking-widest font-mono-ed">
                  hero preview
                </div>
              )}
            </div>
            <div className="p-5">
              <span className="tape">{form.category}</span>
              <h3 className="text-headline text-2xl mt-3">{form.title || "Untitled story"}</h3>
              <p className="text-dek mt-2 text-sm line-clamp-3">{form.dek || "Your dek shows here…"}</p>
              <div className="text-xs font-mono-ed uppercase tracking-widest text-[var(--muted)] mt-3">
                {form.author || "—"} · {form.read_minutes} min
              </div>
            </div>
          </div>
          <p className="text-xs text-[var(--muted)] mt-3 font-mono-ed uppercase tracking-widest">
            Live preview · {isNew ? "draft" : `editing ${initial.slug}`}
          </p>
        </aside>
      </div>
    </div>
  );
}


function ImageUploader({ token, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState("upload"); // upload | url

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Image files only.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max 10 MB.");
      return;
    }
    setUploading(true); setProgress(0);
    try {
      const res = await adminUploadImage(token, file, (e) => {
        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
      });
      onChange(res.url);
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div data-testid="article-hero-uploader">
      {/* Mode toggle */}
      <div className="flex gap-2 mb-3">
        <button type="button"
                onClick={() => setMode("upload")}
                className={`chip ${mode === "upload" ? "chip-active" : ""}`}
                data-testid="hero-mode-upload">
          <Upload size={12} className="inline mr-1" /> Upload
        </button>
        <button type="button"
                onClick={() => setMode("url")}
                className={`chip ${mode === "url" ? "chip-active" : ""}`}
                data-testid="hero-mode-url">
          Paste URL
        </button>
      </div>

      {mode === "upload" ? (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`block cursor-pointer border-2 border-dashed transition ${dragOver ? "border-[var(--rust)] bg-[var(--rust)]/5" : "border-[var(--ink)]"} p-6 text-center`}
          data-testid="hero-dropzone"
        >
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="hero-file-input"
          />
          {uploading ? (
            <div>
              <div className="font-display uppercase tracking-widest text-sm text-[var(--rust)]">Uploading…</div>
              <div className="mt-2 h-2 bg-[var(--paper-deep)] overflow-hidden">
                <div className="h-full bg-[var(--rust)] transition-all" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-xs font-mono-ed mt-2">{progress}%</div>
            </div>
          ) : (
            <div>
              <ImagePlus size={28} className="mx-auto text-[var(--rust)]" />
              <div className="font-display uppercase tracking-widest text-sm mt-2">Drop image or click to upload</div>
              <div className="text-xs text-[var(--muted)] mt-1 font-mono-ed">JPG · PNG · WebP · GIF · max 10 MB</div>
            </div>
          )}
        </label>
      ) : (
        <input
          type="url"
          className="field"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://images.unsplash.com/..."
          data-testid="hero-url-input"
        />
      )}

      {value && (
        <div className="mt-3 relative" data-testid="hero-preview">
          <img src={value} alt="hero preview"
               className="w-full max-h-48 object-cover border-2 border-[var(--ink)]"
               onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
          <button type="button"
                  onClick={() => onChange("")}
                  className="absolute top-2 right-2 bg-[var(--ink)] text-[var(--paper)] p-1.5 hover:bg-[var(--rust)]"
                  title="Clear image"
                  data-testid="hero-clear-button">
            <X size={14} />
          </button>
          <div className="text-[0.65rem] font-mono-ed mt-1 truncate text-[var(--muted)]">{value}</div>
        </div>
      )}
    </div>
  );
}
